import { Button } from "@sellar-io/components";
import {
  CompanyCreditInfoResponseType,
  PaymentLinkGetResponseType,
} from "@sellar-io/api-v2/types";
import {
  formatCurrency,
  getSellarCreditLowBalanceInfo,
} from "@sellar-io/common";
import dayjs from "dayjs";
import { useState } from "react";
import { useNavigate } from "react-router";
import { Icon, ProfilePicture } from "../../components";
import { Alert } from "../../components/alert/Alert";
import { OrderFulfillmentBadge } from "../../components/badge/OrderFulfillmentBadge";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
} from "../../components/dropdown/Dropdown";
import {
  Dialog,
  DialogNavigation,
  DialogCTA,
} from "../../components/modal/Dialog";
import { SellarCreditAddToTabButton } from "../../components/newOrderSummary/Buttons/SellarCreditAddToTabButton";
import { usePaymentTermsIndicatorDirect } from "../../components/paymentTypeIndicator/usePaymentTermsIndicatorDirect";
import { CreditBalance } from "../../components/sidebar/Sidebar";
import {
  ORDER_BUYER,
  ORDER_DETAILS,
  PAYMENT_LINK,
} from "../../constants/routes";
import {
  BasketContextActionEnum,
  useBasketContext,
  useSellarUser,
} from "../../hooks";
import { useCurrentBusinessUser } from "../../hooks/api/v2/business/useCurrentBusinessUser";
import { useParamAsNumber } from "../../hooks/useParamAsNumber";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { getOrderFulfillmentBadgeType } from "../../utils/getOrderFulfillmentBadgeType";
import { CancelOrderDialog } from "../order";
import { SellarCreditCancelWarningDialog } from "../order/modals/SellarCreditCancelWarningDialog";
import { PoweredBySellar } from "../stockList/categoryPage/Storefront/PoweredBySellar";
import { PaymentLinkDrawer } from "./components/PaymentLinkDrawer";
import { InvoiceDrawerTabs } from "./components/PaymentLinkDrawer.shared";

export const SellarCreditPaymentLinkContent = ({
  paymentLink,
  companyCreditInfo,
  isPreview,
}: {
  paymentLink: PaymentLinkGetResponseType;
  companyCreditInfo?: CompanyCreditInfoResponseType;
  isPreview?: boolean;
}) => {
  const { user, changeBusiness } = useSellarUser();
  const { business } = useSellarCurrentBusiness();
  const orderId = useParamAsNumber(PAYMENT_LINK, "orderId");
  if (!orderId) throw new Error("orderId is required");
  const navigate = useNavigate();
  const [{}, dispatchBasket] = useBasketContext();
  const [drawerActive, setDrawerActive] = useState(false);
  const [sellarCreditCancelWarningActive, setSellarCreditCancelWarningActive] =
    useState<boolean>(false);
  const [cancelOrderModalActive, setCancelOrderModalActive] =
    useState<boolean>(false);
  const [businessSwitchModalActive, setBusinessSwitchModalActive] =
    useState(false);
  const [showCantAccessErrorMessage, setShowCantAccessErrorMessage] =
    useState(false);
  const [initialTab, setInitialTab] = useState<InvoiceDrawerTabs>(
    InvoiceDrawerTabs.DETAILS,
  );

  const retailerBusinessUserQuery = useCurrentBusinessUser(
    paymentLink.Retailer.Business.id,
  );

  const retailerBusinessUser = retailerBusinessUserQuery.data;

  const { full } = usePaymentTermsIndicatorDirect({
    indicatorType: "sellarCredit",
  });

  if (paymentLink.paymentType !== "sellarCredit")
    throw new Error("Payment link not available for this payment type");

  const supplier = paymentLink.Supplier;
  const supplierBusiness = supplier.Business;
  const supplierBusinessName = supplierBusiness.name;

  const canAccessSellarCredit = retailerBusinessUser?.canAccessSellarCredit;
  const canViewSellarCreditBalance =
    retailerBusinessUser?.canViewSellarCreditBalance;

  const isViewingAsDifferentBusiness =
    business?.id !== paymentLink.Retailer.Business.id;

  const navigateStorefront = () => {
    navigate(
      ORDER_BUYER.href({ supplierIdOrUrlId: paymentLink.Supplier.urlId }),
    );
  };
  const onClickEdit = async () => {
    if (!canAccessSellarCredit) {
      return setShowCantAccessErrorMessage(true);
    }
    if (isViewingAsDifferentBusiness) {
      return setBusinessSwitchModalActive(true);
    }
    dispatchBasket({
      type: BasketContextActionEnum.CHANGE_ORDER,
      order: paymentLink,
    });
    return navigateStorefront();
  };

  const onClickSwitch = () => {
    changeBusiness(paymentLink.Retailer.Business.id);
    setBusinessSwitchModalActive(false);
  };

  const isSellarCreditBalanceLow = Boolean(
    getSellarCreditLowBalanceInfo({
      creditLimit: companyCreditInfo?.creditLimit ?? 0,
      availableBalance: companyCreditInfo?.creditAmountAvailable ?? 0,
    }).data?.isLow,
  );

  return (
    <>
      <div className="flex w-full flex-col gap-14 md:w-auto">
        <div className="inline-flex w-full max-w-md flex-col items-center justify-start gap-14 px-4 pt-4 sm:p-0">
          <div className="flex flex-col items-center justify-start gap-10 self-stretch">
            <div className="flex flex-col items-center justify-start self-stretch rounded-lg shadow-[0px_16px_32px_0px_rgba(0,0,0,0.16)] outline outline-1 outline-zinc-300">
              <div className="flex flex-col items-center justify-start gap-6 self-stretch rounded-tl-lg rounded-tr-lg border-b border-zinc-300 bg-white px-4 py-6">
                <div className="flex w-full flex-col items-center justify-start">
                  <div className="inline-flex h-6 w-full items-center justify-end gap-2 md:w-96">
                    <Dropdown>
                      <DropdownButton>
                        <Button size={"small"} color={"transparent"}>
                          <Icon icon={"DOTS_VERTICAL"} className="text-black" />
                        </Button>
                      </DropdownButton>
                      <DropdownItems>
                        <DropdownItem
                          className={"text-red-700"}
                          onClick={() =>
                            isPreview
                              ? undefined
                              : setSellarCreditCancelWarningActive(true)
                          }
                        >
                          Cancel order
                        </DropdownItem>
                      </DropdownItems>
                    </Dropdown>
                  </div>
                  <div className="flex w-full max-w-52 flex-col items-center justify-start gap-1">
                    <div className="flex flex-col items-center justify-start gap-3 self-stretch">
                      <ProfilePicture
                        name={supplierBusinessName}
                        logoUrl={supplierBusiness.logoUrl}
                        size={"xl"}
                      />
                      <div className="justify-center self-stretch text-center font-['Inter'] text-2xl leading-loose font-semibold text-zinc-800">
                        {supplierBusinessName}
                      </div>
                    </div>
                    <div className="justify-end self-stretch text-center font-['Inter'] text-sm leading-tight font-medium text-zinc-700">
                      Order for {paymentLink.Retailer.Business.name},{" "}
                      {paymentLink.Retailer.Business.DeliveryAddress.postalCode}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-center justify-start gap-4">
                  <div className="justify-center self-stretch text-center text-2xl leading-loose font-semibold text-zinc-800">
                    {formatCurrency(paymentLink.price)}
                  </div>
                  <div className="flex flex-col items-center justify-start gap-2 self-stretch">
                    <div className="inline-flex items-center justify-start gap-2">
                      <OrderFulfillmentBadge
                        type={getOrderFulfillmentBadgeType({
                          fulfillmentType: paymentLink.fulfillmentType,
                          isSupplier: false,
                        })}
                      />
                      {full}
                    </div>
                    {paymentLink.deliveryPlannedAt && (
                      <div className="text-xs leading-none font-normal text-zinc-700">
                        Estimated delivery:{" "}
                        {dayjs(paymentLink.deliveryPlannedAt).format(
                          "DD/MM/YYYY",
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="w-full max-w-52">
                  {companyCreditInfo?.status === "approved" &&
                    canViewSellarCreditBalance && (
                      <CreditBalance
                        creditAmountAvailable={
                          companyCreditInfo.creditAmountAvailable || 0
                        }
                        creditPermissions={{
                          canView: true,
                          canAccess: true,
                        }}
                        onCloseTooltip={() => {}}
                        showSellarCreditBalanceTooltip={false}
                        className={"border-none"}
                        isSellarCreditBalanceLow={isSellarCreditBalanceLow}
                      />
                    )}
                </div>
              </div>
              <div className="inline-flex items-center justify-start self-stretch bg-white">
                <div
                  className="flex flex-1 cursor-pointer items-center justify-center gap-1 rounded-bl-lg border-r border-zinc-300 bg-white p-4"
                  onClick={() => {
                    if (isPreview) return undefined;
                    setInitialTab(InvoiceDrawerTabs.COMMENTS);
                    setDrawerActive(true);
                  }}
                >
                  <Icon icon={"COMMENT_TEXT_OUTLINE"} />
                  <div className="flex items-center justify-center">
                    <div className="justify-center text-center font-['Inter'] text-sm leading-tight font-semibold text-zinc-600">
                      Add a comment
                    </div>
                  </div>
                </div>
                <div
                  className="flex flex-1 cursor-pointer items-center justify-center gap-1 rounded-br-lg bg-white p-4"
                  onClick={() => {
                    if (isPreview) return undefined;
                    setInitialTab(InvoiceDrawerTabs.DETAILS);
                    setDrawerActive(true);
                  }}
                >
                  <div className="flex items-center justify-center">
                    <div className="justify-center text-center font-['Inter'] text-sm leading-tight font-semibold text-zinc-600">
                      View order
                    </div>
                  </div>
                  <Icon icon={"CHEVRON_RIGHT"} />
                </div>
              </div>
            </div>
            <div className="grid w-full grid-cols-2 gap-2">
              <div className="col-span-1 flex w-full flex-col gap-2">
                <Button
                  className={"col-span-1 w-full"}
                  color={"secondary"}
                  onClick={() => (isPreview ? undefined : onClickEdit())}
                >
                  Edit order
                </Button>
              </div>
              <div className="col-span-1 w-full">
                {isPreview ? (
                  <Button className={"w-full"}>
                    <Icon icon={"SELLAR_NEW"} />
                    Add to tab
                  </Button>
                ) : (
                  <>
                    {canAccessSellarCredit ? (
                      <SellarCreditAddToTabButton
                        initiator={"supplier"}
                        orderId={paymentLink.id}
                        className={"md:w-full"}
                      />
                    ) : (
                      <Button
                        className={"w-full"}
                        onClick={() => setShowCantAccessErrorMessage(true)}
                      >
                        <Icon icon={"SELLAR_NEW"} />
                        Add to tab
                      </Button>
                    )}
                  </>
                )}
              </div>
              <div className="col-span-2 mt-2">
                {showCantAccessErrorMessage && (
                  <Alert
                    className={"max-w-md"}
                    type="error"
                    title={`${user?.email} doesn't have Sellar Credit Access`}
                    message={
                      "You must be given permission to use Sellar Credit by your company."
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        <PoweredBySellar />
      </div>
      <PaymentLinkDrawer
        active={drawerActive}
        setActive={setDrawerActive}
        order={paymentLink}
        initialTab={initialTab}
      />
      <SellarCreditCancelWarningDialog
        active={sellarCreditCancelWarningActive}
        type={"buyer"}
        onEditOrder={() => setSellarCreditCancelWarningActive(false)}
        onDismiss={() => {
          setSellarCreditCancelWarningActive(false);
        }}
        onConfirmCancel={() => {
          setSellarCreditCancelWarningActive(false);
          setCancelOrderModalActive(true);
        }}
      />
      <CancelOrderDialog
        active={cancelOrderModalActive}
        order={paymentLink}
        onDismiss={() => setCancelOrderModalActive(false)}
        onSubmit={() =>
          navigate(`${ORDER_DETAILS.href({ orderId })}?tab=All+orders`)
        }
      />
      <Dialog
        active={businessSwitchModalActive}
        onDismiss={() => setBusinessSwitchModalActive(false)}
      >
        <DialogNavigation
          title={`Edit order for ${paymentLink.Retailer.Business.name}, ${paymentLink.Retailer.Business.DeliveryAddress.postalCode}`}
          description={`You're viewing as ${business?.name}. Click continue to switch to ${paymentLink.Retailer.Business.name}`}
          leftIcon={"CHEVRON_DOWN"}
          onClickLeftIcon={() => setBusinessSwitchModalActive(false)}
        />
        <DialogCTA>
          <Button onClick={onClickSwitch}>Continue</Button>
        </DialogCTA>
      </Dialog>
    </>
  );
};
